import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PawPrint, Plus, X, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";

interface AnimalSearchResult {
  animalId: string;
  name: string;
  species: string | null;
}

interface AnimalSearchSelectProps {
  selectedIds: string[];
  selectedNames: Map<string, string>;
  onAdd: (animalId: string, name: string) => void;
  onRemove: (animalId: string) => void;
  enabled?: boolean;
  showLabel?: boolean;
  testIdPrefix?: string;
}

export function AnimalSearchSelect({
  selectedIds,
  selectedNames,
  onAdd,
  onRemove,
  enabled = true,
  showLabel = true,
  testIdPrefix = "search-select",
}: AnimalSearchSelectProps) {
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];

  const { data: searchData } = useQuery<{ animals: AnimalSearchResult[] }>({
    queryKey: ["/api/tasks/animal-search?q=" + encodeURIComponent(search)],
    enabled: search.length >= 2 && enabled,
  });

  useEffect(() => {
    if (showSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch]);

  function handleAdd(animalId: string, name: string) {
    if (!selectedIds.includes(animalId)) {
      onAdd(animalId, name);
    }
    setSearch("");
    setShowSearch(false);
  }

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="flex items-center gap-2">
          <Label className="flex items-center gap-1">
            <PawPrint className="w-3 h-3" /> Animals
          </Label>
          {!showSearch && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs text-muted-foreground gap-1"
              onClick={() => setShowSearch(true)}
              data-testid={`${testIdPrefix}-button-add-animal`}
            >
              <Plus className="w-3 h-3" /> Add
            </Button>
          )}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map(id => (
            <Badge key={id} variant="secondary" className="text-xs" data-testid={`${testIdPrefix}-badge-animal-${id}`}>
              <PawPrint className="w-3 h-3 mr-1" />
              {selectedNames.get(id) || id}
              <button className="ml-1" onClick={() => onRemove(id)}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {(showSearch || !showLabel) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Input
              ref={inputRef}
              placeholder="Search animals..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-input-animal-search`}
            />
            {showLabel && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => { setShowSearch(false); setSearch(""); }}
                data-testid={`${testIdPrefix}-button-close-animal-search`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {searchData?.animals && searchData.animals.length > 0 && (
            <div className="border rounded-md max-h-32 overflow-y-auto">
              {searchData.animals
                .filter(a => !selectedIds.includes(a.animalId))
                .map(a => (
                  <button
                    key={a.animalId}
                    className="w-full text-left px-2 py-1 text-sm hover-elevate"
                    onClick={() => handleAdd(a.animalId, a.name)}
                    data-testid={`${testIdPrefix}-button-link-animal-${a.animalId}`}
                  >
                    {a.name} {followedIds.includes(a.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle" data-testid={`heart-followed-${a.animalId}`} />} ({a.animalId}) {a.species && `- ${a.species}`}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
